<template>
  <v-form v-model="formValid" ref="form">
    <v-row no-gutters class="mb-2">
      <v-col cols="12" lg="2" md="5">
        Legal Business Name
      </v-col>
      <v-col cols="12" lg="5" md="7">
        <v-row>
          <v-col>
            <v-text-field
              filled
              dense
              v-model="vm.businessName"
              :label="`Company${vm.soleProprietor ? '' : '*'}`"
              :disabled="!editMode"
              :rules="[vm.soleProprietor || rules.required('Company')]"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <revision-request-note
              v-for="note in businessNameNotes"
              :note="note.note"
              v-bind:key="note.note"
            ></revision-request-note>
          </v-col>
        </v-row>
      </v-col>
      <v-col
        cols="12"
        lg="5"
        :order="$vuetify.breakpoint.lgAndUp ? 'last' : 'first'"
      >
        <div class="text-right mb-4 mb-lg-0" v-if="showEditButton === true">
          <v-btn
            @click="editMode = true"
            text
            color="primary"
            class="underline-btn"
            v-if="!editMode"
          >
            <v-icon left>mdi-pencil</v-icon>
            Edit
          </v-btn>
          <template v-else>
            <v-btn
              @click="
                editMode = false;
                reset();
              "
            >
              Cancel
            </v-btn>
            <v-btn
              :disabled="!formValid"
              class="ml-6"
              color="primary"
              depressed
              @click="
                editMode = false;
                save();
              "
            >
              Save Changes
            </v-btn>
          </template>
        </div>
      </v-col>
    </v-row>
    <v-divider />

    <v-row class="mt-8 mb-2" no-gutters>
      <v-col cols="12" lg="2" md="5">
        Doing Business As Name
      </v-col>
      <v-col cols="12" lg="5" md="7">
        <v-row>
          <v-col>
            <v-text-field
              v-model="vm.dbaName"
              :disabled="!editMode"
              dense
              filled
              label="DBA Name"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <revision-request-note
              v-for="note in dbaNameNotes"
              v-bind:key="note.note"
              :note="note.note"
            ></revision-request-note>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-divider />

    <v-row class="mt-8 mb-2" no-gutters>
      <v-col cols="12" lg="2" md="5">
        Individual/Sole Proprietor
      </v-col>
      <v-col cols="12" lg="5" md="7">
        <v-row>
          <v-col>
            <v-checkbox
              v-model="vm.soleProprietor"
              :disabled="!editMode"
              class="mt-0 pt-0"
              label="I am registering as an individual/sole proprietor"
            />
          </v-col>
        </v-row>

        <template v-if="vm.soleProprietor">
          <v-row no-gutters>
            <v-col class="pr-md-3" cols="12" md="6">
              <v-text-field
                v-model="vm.firstName"
                :disabled="!editMode"
                :rules="[rules.required('First Name')]"
                dense
                filled
                label="First Name*"
              ></v-text-field>
            </v-col>
            <v-col class="pl-md-3" cols="12" md="6">
              <v-text-field
                v-model="vm.lastName"
                :disabled="!editMode"
                :rules="[rules.required('Last Name')]"
                dense
                filled
                label="Last Name*"
              ></v-text-field>
            </v-col>
          </v-row>
          <date-field
            v-model="vm.dob"
            :disabled="!editMode"
            :maxDate="getNow()"
            :rules="[rules.required('Date of Birth')]"
            label="Date of Birth*"
          >
          </date-field>
        </template>
        <v-row>
          <v-col>
            <revision-request-note
              v-for="note in soleProprietorNotes"
              v-bind:key="note.note"
              :note="note.note"
            ></revision-request-note>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-divider />

    <v-row class="mt-8 mb-2" no-gutters>
      <v-col cols="12" lg="2" md="5">
        Type of product(s)/service(s) offered
      </v-col>
      <v-col cols="12" lg="5" md="7">
        <v-row>
          <v-col>
            <v-text-field
              v-model="vm.servicesOffered"
              :disabled="!editMode"
              :label="
                'Type of product(s)/service(s) offered' +
                  (servicesOfferedRequired ? '*' : '')
              "
              :rules="[
                rules.condRequired(
                  'Type of product(s)/service(s) offered',
                  servicesOfferedRequired
                )
              ]"
              dense
              filled
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <revision-request-note
              v-for="note in servicesOfferedNotes"
              v-bind:key="note.note"
              :note="note.note"
            ></revision-request-note>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-divider />

    <v-row class="mt-8 mb-2" no-gutters>
      <v-col cols="12" lg="2" md="5">
        Office Address
      </v-col>
      <v-col cols="12" lg="5" md="7">
        <v-text-field
          v-model="vm.officeAddress.addrLine1"
          :disabled="!editMode"
          :rules="[rules.required()]"
          class="office-address-input"
          dense
          filled
          label="Address Line 1*"
          placeholder=""
        ></v-text-field>
        <v-text-field
          v-model="vm.officeAddress.addrLine2"
          :disabled="!editMode"
          dense
          filled
          label="Suite, APT (optional)"
        ></v-text-field>

        <v-row no-gutters>
          <v-col class="pr-md-3" cols="12" md="6">
            <v-text-field
              v-model="vm.officeAddress.city"
              :disabled="!editMode"
              :rules="[rules.required()]"
              dense
              filled
              label="City*"
            ></v-text-field>
          </v-col>
          <v-col class="pl-md-3" cols="12" md="6">
            <v-autocomplete
              v-model="vm.officeAddress.state"
              :disabled="!editMode"
              :items="states"
              :rules="[rules.required()]"
              dense
              filled
              item-text="code"
              item-value="code"
              label="State*"
            ></v-autocomplete>
          </v-col>
        </v-row>
        <v-text-field
          v-model="vm.officeAddress.zip"
          :disabled="!editMode"
          :rules="[rules.required(), rules.zip]"
          dense
          filled
          label="Zip Code*"
        ></v-text-field>
        <v-autocomplete
          v-model="vm.officeAddress.country"
          :disabled="!editMode"
          :items="countries"
          :rules="[rules.required()]"
          dense
          filled
          item-text="name"
          item-value="code"
          label="Country*"
        ></v-autocomplete>
        <v-text-field
          v-model="vm.phoneNumber"
          :disabled="!editMode"
          :rules="[rules.required('Phone Number'), rules.phoneNumber]"
          dense
          filled
          label="Phone Number*"
        ></v-text-field>
        <v-row>
          <v-col>
            <revision-request-note
              v-for="note in designatedAddressNotes"
              v-bind:key="note.note"
              :note="note.note"
            ></revision-request-note>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-divider />

    <v-row class="mt-8 mb-2" no-gutters>
      <v-col cols="12" lg="2" md="5">
        Mailing Address
      </v-col>
      <v-col cols="12" lg="5" md="7">
        <v-checkbox
          v-model="vm.mailingAddressSameAsDesignated"
          :disabled="!editMode"
          class="mt-0 pt-0"
          label="Same as Office Address"
        />

        <div v-show="!vm.mailingAddressSameAsDesignated">
          <v-text-field
            v-model="vm.mailingAddress.addrLine1"
            :disabled="!editMode"
            :rules="[vm.mailingAddressSameAsDesignated || rules.required()]"
            class="mailing-address-input"
            dense
            filled
            label="Address Line 1*"
            placeholder=""
          ></v-text-field>
          <v-text-field
            v-model="vm.mailingAddress.addrLine2"
            :disabled="!editMode"
            dense
            filled
            label="Suite, APT (optional)"
          ></v-text-field>

          <v-row no-gutters>
            <v-col class="pr-md-3" cols="12" md="6">
              <v-text-field
                v-model="vm.mailingAddress.city"
                :disabled="!editMode"
                :rules="[vm.mailingAddressSameAsDesignated || rules.required()]"
                dense
                filled
                label="City*"
              ></v-text-field>
            </v-col>
            <v-col class="pl-md-3" cols="12" md="6">
              <v-autocomplete
                v-model="vm.mailingAddress.state"
                :disabled="!editMode"
                :items="states"
                :rules="[vm.mailingAddressSameAsDesignated || rules.required()]"
                dense
                filled
                item-text="code"
                item-value="code"
                label="State*"
              ></v-autocomplete>
            </v-col>
          </v-row>
          <v-text-field
            v-model="vm.mailingAddress.zip"
            :disabled="!editMode"
            :rules="
              vm.mailingAddressSameAsDesignated
                ? []
                : [rules.required(), rules.zip]
            "
            dense
            filled
            label="Zip Code*"
          ></v-text-field>
          <v-autocomplete
            v-model="vm.mailingAddress.country"
            :disabled="!editMode"
            :items="countries"
            :rules="[vm.mailingAddressSameAsDesignated || rules.required()]"
            dense
            filled
            item-text="name"
            item-value="code"
            label="Country*"
          ></v-autocomplete>
        </div>
        <v-row>
          <v-col>
            <revision-request-note
              v-for="note in mailingAddressNotes"
              v-bind:key="note.note"
              :note="note.note"
            ></revision-request-note>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-divider />

    <v-row class="mt-8 mb-2" no-gutters>
      <v-col cols="12" lg="2" md="5">
        Tax Identification
      </v-col>
      <v-col cols="12" lg="5" md="7">
        <v-row no-gutters>
          <v-col class="pr-md-3" cols="12" md="6">
            <v-autocomplete
              v-model="vm.tinType"
              :disabled="!editMode"
              :items="['SSN', 'EIN']"
              :rules="[val => val !== 'UNKNOWN' || 'TIN Type is Required']"
              dense
              filled
              label="TIN Type*"
            ></v-autocomplete>
          </v-col>
          <v-col class="pl-md-3" cols="12" md="6">
            <v-text-field
              v-model="vm.tin"
              :disabled="!editMode"
              :rules="[
                rules.required('Tax ID Number'),
                rules.nDigits('Tax ID Number', 9)
              ]"
              dense
              filled
              label="Tax ID Number*"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <revision-request-note
              v-for="note in tinNotes"
              v-bind:key="note.note"
              :note="note.note"
            ></revision-request-note>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-divider />

    <v-row class="mt-8 mb-2" no-gutters>
      <v-col cols="12" lg="2" md="5">
        Other (optional)
      </v-col>
      <v-col cols="12" lg="5" md="7">
        <v-text-field
          v-model="vm.duns"
          :disabled="!editMode"
          :rules="[rules.nDigits('DUNS', 9)]"
          dense
          filled
          hint="Dun & Bradstreet provides businesses with a unique nine digit identification number."
          label="DUNS"
        ></v-text-field>

        <v-text-field
          v-model="vm.npi"
          :disabled="!editMode"
          :rules="[rules.nDigits('NPI', 10)]"
          dense
          filled
          hint="NPI is a unique ID number for covered healthcare providers."
          label="National Provider Identifier"
        ></v-text-field>
        <v-row>
          <v-col>
            <revision-request-note
              v-for="note in otherNotes"
              v-bind:key="note.note"
              :note="note.note"
            ></revision-request-note>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import {
  clientConfigAPI,
  companyAPI,
  revisionRequestNotesAPI
} from "@/services/api";
import { get, put } from "@/services/request";
import cloneDeep from "lodash/cloneDeep";
import { datePickerNow } from "@/services/util";
import DateField from "@/components/DateField";
import RevisionRequestNote from "@/components/RevisionRequestNote";
import * as rules from "@/services/rules";
import hasConstant from "@/mixins/has-constant";
import { autocompleteAddress } from "@/services/address-autocomplete";

export default {
  name: "Company",
  mixins: [hasConstant],
  props: {
    showEditButton: {
      type: Boolean,
      default: true
    },
    startInEditMode: {
      type: Boolean,
      default: false
    },
    clientId: {
      //If exists, used to load client-specific configuration
      type: Number,
      default: undefined
    }
  },
  data: function() {
    return {
      vm: {
        officeAddress: {
          country: "US"
        },
        mailingAddress: {
          country: "US"
        },
        tinType: "UNKNOWN"
      },
      notes: [],
      editMode: this.startInEditMode,
      formValid: false,
      servicesOfferedRequired: true,
      rules
    };
  },
  watch: {
    formValid() {
      this.validate();
    },
    vm: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.validate();
        });
      }
    }
  },
  created() {
    get(companyAPI).then(response => this.handleResponse(response));
    if (this.clientId) {
      get(clientConfigAPI.getUrl(this.clientId)).then(response => {
        if (response.data.requirementMap) {
          this.servicesOfferedRequired =
            response.data.requirementMap.SERVICES_OFFERED !== false;
          this.$nextTick(() => {
            this.validate();
          });
        }
      });
    }
    this.loadNotes();
  },
  mounted() {
    const input1 = document.querySelector(
      ".office-address-input input[type=text]"
    );
    const input2 = document.querySelector(
      ".mailing-address-input input[type=text]"
    );

    this.autocompleteAddr1 = autocompleteAddress(
      input1,
      newAddress => (this.vm.officeAddress = newAddress)
    );
    this.autocompleteAddr2 = autocompleteAddress(
      input2,
      newAddress => (this.vm.mailingAddress = newAddress)
    );
  },
  beforeDestroy() {
    this.autocompleteAddr1.destroy();
    this.autocompleteAddr2.destroy();
  },
  methods: {
    reset() {
      this.vm = cloneDeep(this.lastVm);
    },
    save() {
      return put(companyAPI, this.vm).then(response =>
        this.handleResponse(response)
      );
    },
    handleResponse(response) {
      if (response && response.data) {
        response.data.officeAddress.country =
          response.data.officeAddress.country || "US";
        response.data.mailingAddress.country =
          response.data.mailingAddress.country || "US";
        this.vm = response.data;
        this.lastVm = cloneDeep(response.data);
        this.loadNotes();
        this.validate();
      }
    },
    validate() {
      if (this.$refs.form) {
        this.$refs.form.validate();
      }
      this.$emit("form-valid", this.formValid);
    },
    getNow: datePickerNow,
    loadNotes() {
      get(revisionRequestNotesAPI, {
        type: "AccountInfo",
        incompleteOnly: true
      }).then(response => {
        if (response && response.data) {
          this.notes = response.data;
        }
      });
    }
  },
  computed: {
    businessNameNotes() {
      return this.notes.filter(
        note => !note.completed && note.vendorData === "BUSINESS_NAME"
      );
    },
    dbaNameNotes() {
      return this.notes.filter(
        note => !note.completed && note.vendorData === "DBA_NAME"
      );
    },
    servicesOfferedNotes() {
      return this.notes.filter(
        note => !note.completed && note.vendorData === "SERVICES_OFFERED"
      );
    },
    soleProprietorNotes() {
      return this.notes.filter(
        note => !note.completed && note.vendorData === "SOLE_PROPRIETOR"
      );
    },
    designatedAddressNotes() {
      return this.notes.filter(
        note => !note.completed && note.vendorData === "DESIGNATED_ADDRESS"
      );
    },
    mailingAddressNotes() {
      return this.notes.filter(
        note => !note.completed && note.vendorData === "MAILING_ADDRESS"
      );
    },
    tinNotes() {
      return this.notes.filter(
        note =>
          !note.completed &&
          (note.vendorData === "TIN" || note.vendorData === "TIN_TYPE")
      );
    },
    phoneNumberNotes() {
      return this.notes.filter(
        note => !note.completed && note.vendorData === "PHONE_NUMBER"
      );
    },
    otherNotes() {
      return this.notes.filter(
        note =>
          !note.completed &&
          (note.vendorData === "DUNS" || note.vendorData === "NPI")
      );
    }
  },
  components: {
    DateField,
    RevisionRequestNote
  }
};
</script>

<style lang="sass" scoped>
.cancel-btn
  color: $lightish-grey
</style>
